import * as Yup from 'yup';
import moment from 'moment';
import { isEmpty, isNil, noop } from 'lodash';
import type { TFunction } from 'react-i18next';
// Import Types
import type { CurrencyAmounts, PartnerCurrency } from 'types';
// Import Enums
import { AwardBudgetSetupTypesEnum, AwardConfFieldNamesEnum, MetricTypeEnum } from 'enums';
// Import Constants
import {
    l,
    MonthDayYearWithTimeFormat,
    MonthDayYearFormat,
    CommunicationAvailabilityScheduleTypeLabels,
    DynamicBonusTypes,
    ScheduleModalViews,
    ExpirationLabels,
    ExpirationTypes,
    ZERO_TIME_WITHOUT_SECONDS,
} from 'constants/common';
import { MAX_ACHIEVEMENT_CHALLENGES_COUNT } from 'constants/achievement';
import { POSITIVE_AMOUNT_REGEXP } from 'constants/regexp';
// Import Services
import { isSchedulerUnitPairsInvalid } from 'services/schedular';
// Import Helpers
import {
    getDateFormat,
    customMomentWithoutTimezoneConversion,
    customMomentWithTimezoneConversion,
} from 'services/Helpers';
// Import Validations
import { isPositiveInteger } from './yup.validator';
import { yupSchemaValidate } from './yupUtils';

// In all schema can not be all like together.
// Which message must to get or object white params.
// In CJ case we are usage only field name label but also can use or change logic like label:{params:[]}.
// Now CJ logic {label: l.[property], fieldName: string | {value: string, isTranslated: boolean}}.

const yupSchemaExtender = (yupSchema, { Validation, CustomAttributes }) => {
    if (!isNil(yupSchema) && (yupSchema.type === Yup.string().type || yupSchema.type === Yup.number().type)) {
        const message: { fieldName?: string } = {};
        if (!isNil(CustomAttributes?.DisplayName)) {
            message.fieldName = CustomAttributes.DisplayName;
        }
        if (!isNil(Validation?.DecimalPoints)) {
            yupSchema = yupSchema.decimalPointsValidation(Validation.DecimalPoints, message);
        }
        if (!isNil(Validation?.MaxValue)) {
            yupSchema = yupSchema.maxValueValidation(Validation.MaxValue, message);
        }
        if (!isNil(Validation?.MinValue)) {
            yupSchema = yupSchema.minValueValidation(Validation.MinValue, message);
        }
        if (!isNil(Validation?.StringPattern)) {
            yupSchema = yupSchema.stringPatternValidation(Validation.StringPattern, message);
        }
    }

    if (!isNil(yupSchema) && yupSchema.type === Yup.array().type) {
        const message: { fieldName?: string } = {};
        if (!isNil(CustomAttributes?.DisplayName)) {
            message.fieldName = CustomAttributes.DisplayName;
        }
        if (!isNil(Validation?.MaxLength)) {
            yupSchema = yupSchema.maxLengthValidation(Validation.MaxLength, message);
        }
    }
    return yupSchema;
};

export const createBudgetValidationSchema = (
    t: TFunction,
    getBudgetFieldName: (fieldName: AwardConfFieldNamesEnum) => string,
    isRequiredPromoBudgetLimit = false,
    isBudgetGroupNameValidationEnabled = false,
    isBonusBlocksValidationEnabled = false,
    isNoLimitAvailable = true,
    existingNames: string[] = [],
) => {
    return Yup.object()
        .shape({
            ...(isBudgetGroupNameValidationEnabled
                ? {
                      name: Yup.string()
                          .trim()
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.BudgetGroupName) }))
                          .min(3, t(l.MustContainMinimumCharacters, { fieldName: t(l.BudgetGroupName), count: 3 }))
                          .max(50, t(l.MustContainMaximumCharacters, { fieldName: t(l.BudgetGroupName), count: 50 }))
                          .notOneOf(existingNames, t(l.BudgetGroupNameAlreadyExists)),
                  }
                : {}),
            ...(isBonusBlocksValidationEnabled
                ? {
                      bonusBlocks: Yup.object()
                          .default({})
                          .when(
                              getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey),
                              (activeTabKey, schema) => {
                                  if (isNoLimitAvailable && activeTabKey === AwardBudgetSetupTypesEnum.NoLimit) {
                                      return schema;
                                  }

                                  return schema
                                      .test(
                                          'bonus-blocks-required',
                                          t(l.CannotBeEmpty, { fieldName: t(l.BonusBlocks) }),
                                          (value) => {
                                              return !isEmpty(value);
                                          },
                                      )
                                      .test('bonus-blocks-positive-rate', t(l.MustBeGreaterThenZero), (value) => {
                                          if (isEmpty(value)) {
                                              return true;
                                          }

                                          return Object.values(value).every(({ rate }) => {
                                              const rateValue = Number(rate);
                                              return Number.isFinite(rateValue) && rateValue > 0;
                                          });
                                      });
                              },
                          ),
                  }
                : {}),
            [AwardConfFieldNamesEnum.budgetSetupActiveTabKey]: Yup.string()
                .default(AwardBudgetSetupTypesEnum.NoLimit)
                .when([], {
                    is: () => isRequiredPromoBudgetLimit,
                    then: (schema) =>
                        schema.notOneOf([AwardBudgetSetupTypesEnum.NoLimit], t(l.BudgetLimitRequiredForAwards)),
                }),
            [AwardConfFieldNamesEnum.budgetPeriod]: Yup.object()
                .shape({
                    [AwardBudgetSetupTypesEnum.CountLimit]: Yup.number()
                        .nullable(true)
                        .when(
                            getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey),
                            (activeTabKey, schema) => {
                                if (activeTabKey === AwardBudgetSetupTypesEnum.CountLimit) {
                                    return schema.required(t(l.CannotBeEmpty, { fieldName: t(l.BudgetPeriod) }));
                                }
                                return schema;
                            },
                        ),
                    [AwardBudgetSetupTypesEnum.AmountLimit]: Yup.number()
                        .nullable(true)
                        .when(
                            getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey),
                            (activeTabKey, schema) => {
                                if (activeTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
                                    return schema.required(t(l.CannotBeEmpty, { fieldName: t(l.BudgetPeriod) }));
                                }
                                return schema;
                            },
                        ),
                })
                .default({}),
            [AwardConfFieldNamesEnum.limitBehavior]: Yup.object()
                .shape({
                    [AwardBudgetSetupTypesEnum.CountLimit]: Yup.number()
                        .nullable(true)
                        .when(
                            getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey),
                            (activeTabKey, schema) => {
                                if (activeTabKey === AwardBudgetSetupTypesEnum.CountLimit) {
                                    return schema.required(t(l.CannotBeEmpty, { fieldName: t(l.LimitBehavior) }));
                                }
                                return schema;
                            },
                        ),
                    [AwardBudgetSetupTypesEnum.AmountLimit]: Yup.number()
                        .nullable(true)
                        .when(
                            getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey),
                            (activeTabKey, schema) => {
                                if (activeTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
                                    return schema.required(t(l.CannotBeEmpty, { fieldName: t(l.LimitBehavior) }));
                                }
                                return schema;
                            },
                        ),
                })
                .default({}),
            [AwardConfFieldNamesEnum.countLimitCount]: Yup.number()
                .nullable(true)
                .when(getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey), (activeTabKey, schema) => {
                    if (activeTabKey === AwardBudgetSetupTypesEnum.CountLimit) {
                        return schema
                            .required(t(l.CannotBeEmpty, { fieldName: t(l.AwardCount) }))
                            .integer(t(l.MustBePositiveInteger, { fieldName: t(l.AwardCount) }))
                            .min(1, t(l.MustBePositiveInteger, { fieldName: t(l.AwardCount) }));
                    }
                    return schema;
                }),
            [AwardConfFieldNamesEnum.amountLimitAmount]: Yup.number()
                .nullable(true)
                .when(getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey), (activeTabKey, schema) => {
                    if (activeTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
                        return schema
                            .required(t(l.CannotBeEmpty, { fieldName: t(l.Amount) }))
                            .moreThan(0, t(l.MustBeBiggerThan, { fieldName: t(l.Amount), count: 0 }))
                            .test(
                                'max-two-decimals',
                                t(l.NumberMustHaveMaxNDecimals, { decimals: 2 }),
                                (value) =>
                                    value == null ||
                                    (typeof value === 'number' && Math.round(value * 100) / 100 === value),
                            );
                    }
                    return schema;
                }),
            [AwardConfFieldNamesEnum.amountLimitCurrency]: Yup.string()
                .nullable(true)
                .when(getBudgetFieldName(AwardConfFieldNamesEnum.budgetSetupActiveTabKey), (activeTabKey, schema) => {
                    if (activeTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
                        return schema.required(t(l.CannotBeEmpty, { fieldName: t(l.Currency) }));
                    }
                    return schema;
                }),
        })
        .default({
            ...(isBudgetGroupNameValidationEnabled ? { name: '' } : {}),
            ...(isBonusBlocksValidationEnabled ? { bonusBlocks: {} } : {}),
            [AwardConfFieldNamesEnum.budgetSetupActiveTabKey]: AwardBudgetSetupTypesEnum.NoLimit,
            [AwardConfFieldNamesEnum.budgetPeriod]: {},
            [AwardConfFieldNamesEnum.limitBehavior]: {},
            [AwardConfFieldNamesEnum.countLimitCount]: null,
            [AwardConfFieldNamesEnum.amountLimitAmount]: null,
            [AwardConfFieldNamesEnum.amountLimitCurrency]: null,
        });
};

const CJStaticTypeSchemaCreator = (option) => {
    const { Required, CustomAttributes } = option;
    return Required
        ? Yup.object({
              value: yupSchemaExtender(
                  Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: { value: CustomAttributes?.DisplayName },
                  }),
                  option,
              ).nullable(true),
          })
        : Yup.object({
              value: yupSchemaExtender(Yup.string(), option).nullable(true),
          }).nullable(true);
};

const CJDoubleValidationSchema = (option) => {
    const { Required, CustomAttributes } = option;
    return Required
        ? Yup.object({
              value: yupSchemaExtender(
                  Yup.string()
                      .required({
                          label: l.CannotBeEmpty,
                          fieldName: { value: CustomAttributes?.DisplayName },
                      })
                      .negativeValueValidation({
                          fieldName: { value: CustomAttributes?.DisplayName, isTranslated: true },
                      }),
                  option,
              ).nullable(true),
          })
        : Yup.object({
              value: yupSchemaExtender(Yup.string(), option).nullable(true),
          }).nullable(true);
};

const CJVisualizationStepTemplateValidationSchema = () => {
    return Yup.object({
        name: Yup.string()
            .required({
                label: l.CannotBeEmpty,
                fieldName: l.Name,
            })
            .min(2, { label: l.MustContainMinimumCharacters, fieldName: l.Name, count: 2 })
            .max(250, { label: l.MustContainMaximumCharacters, fieldName: l.Name, count: 250 })
            .nullable(true),
    }).nullable(true);
};

const CJVisualizationStepIconValidationSchema = () => {
    return Yup.object({
        icon: Yup.string()
            .required({
                label: l.CannotBeEmpty,
                fieldName: l.Icon,
            })
            .nullable(true),
    }).nullable(true);
};

const CJVisualizationButtonValidationSchema = () => {
    return Yup.object({
        name: Yup.string()
            .required({
                label: l.CannotBeEmpty,
                fieldName: l.ButtonName,
            })
            .max(50, { label: l.MustContainMaximumCharacters, fieldName: l.ButtonName, count: 50 })
            .nullable(true),
    }).nullable(true);
};

const CJVisualizationProgressTitleValidationSchema = () => {
    return Yup.object({
        name: Yup.string()
            .required({
                label: l.CannotBeEmpty,
                fieldName: l.Title,
            })
            .nullable(true),
    }).nullable(true);
};

const IntegerValidationSchema = (option, isTranslated = true) => {
    const { CustomAttributes, Required } = option;
    return Required
        ? Yup.object({
              value: yupSchemaExtender(
                  Yup.number().required({
                      label: l.CannotBeEmpty,
                      fieldName: { value: CustomAttributes?.DisplayName, isTranslated },
                  }),
                  option,
              ).nullable(true),
          })
        : Yup.object({
              value: yupSchemaExtender(Yup.number(), option).nullable(true),
          }).nullable(true);
};

const CJTimeStampValidationSchema = () => {
    return Yup.object({
        value: Yup.string().test(
            'value',
            {
                label: l.InCorrectDuration,
            },
            function (value: string) {
                const regex = /(\d*)\.(\d*):(\d*):(\d*)/;
                const matches = value.match(regex);

                if (isNil(matches)) return false;

                const hours = +matches[2];
                const minutes = +matches[3];

                return hours < 24 && minutes < 60;
            },
        ),
    });
};

const CJIntegerValidationSchema = (option) => CJDoubleValidationSchema(option);

const CJTemplateIdValidationSchema = (option) => IntegerValidationSchema(option, false);

const CJStringValidationSchema = (option) => CJStaticTypeSchemaCreator(option);

const CJBestMessageSenderValidationSchema = (option) => {
    const { Required } = option;
    const validation = Required
        ? Yup.object({
              templateMessageConfig: Yup.object().test(
                  'custom-object-validation',
                  { label: l.BlockIsInvalid, fieldName: l.TemplateMessageConfig },
                  (channels) => {
                      if (!channels || isEmpty(channels)) {
                          return false; // An empty/null object is not valid
                      }
                      const enabledChannels = Object.values(channels).filter((channel: AnyObject) => channel.isEnabled);

                      return enabledChannels.every((channel: AnyObject) => channel.template?.value);
                  },
              ),
          })
        : Yup.object().nullable(true);

    return validation;
};

const CJMoneyValidationSchema = (option) => {
    const { Required } = option;
    return Required
        ? Yup.object({
              amount: yupSchemaExtender(
                  Yup.string().required({ label: l.CannotBeEmpty, fieldName: l.Amount }).negativeValueValidation({
                      fieldName: l.Amount,
                  }),
                  {
                      ...option,
                      CustomAttributes: { DisplayName: l.Amount },
                  },
              ),
              currencyCode: Yup.string().required({
                  label: l.CannotBeEmpty,
                  fieldName: l.Currency,
              }),
          })
        : Yup.object({
              amount: yupSchemaExtender(
                  Yup.string()
                      .test(
                          'amount',
                          {
                              label: l.CannotBeEmpty,
                              fieldName: l.Amount,
                          },
                          function (value) {
                              const { currencyCode } = this.parent;

                              return !(
                                  !isNil(currencyCode) &&
                                  !isEmpty(currencyCode) &&
                                  (isNil(value) || isEmpty(value))
                              );
                          },
                      )
                      .negativeValueValidation({
                          fieldName: l.Amount,
                      }),
                  {
                      ...option,
                      CustomAttributes: { DisplayName: l.Amount },
                  },
              ).nullable(true),

              currencyCode: Yup.string().test(
                  'currencyCode',
                  {
                      label: l.CannotBeEmpty,
                      fieldName: l.Currency,
                  },
                  function (value) {
                      const { amount } = this.parent;

                      return !(!isNil(amount) && !isEmpty(amount) && (isNil(value) || isEmpty(value)));
                  },
              ),
          });
};

const CJProviderIdValidationSchema = (option) => IntegerValidationSchema(option);

const CJSegmentIdValidationSchema = (option) => IntegerValidationSchema(option);

const CJPartnerBonusIdValidationSchema = (option) => IntegerValidationSchema(option, false);

const CJBonusAmountInputValidationSchema = (bonusDetail, isRequired) => {
    const { MinAmount, MaxAmount, CurrencyCode } = bonusDetail;

    return isRequired
        ? Yup.object({
              amount: Yup.number()
                  .required({ label: l.CannotBeEmpty, fieldName: { value: CurrencyCode, isTranslated: true } }) // TODO: maybe l.Amount
                  .minValueValidation(
                      { Value: MinAmount, IsIncluded: MinAmount > 0 },
                      {
                          fieldName: { value: CurrencyCode, isTranslated: true },
                          count: { value: +MinAmount, isTranslated: true },
                      },
                  )
                  .max(MaxAmount, {
                      label: l.MustBeSmallerThan,
                      fieldName: { value: CurrencyCode, isTranslated: true },
                      count: { value: +MaxAmount, isTranslated: true },
                  })
                  .nullable(true),
          })
        : Yup.object({
              amount: Yup.number()
                  .minValueValidation(
                      { Value: MinAmount, IsIncluded: MinAmount > 0 },
                      {
                          fieldName: { value: CurrencyCode, isTranslated: true },
                          count: { value: +MinAmount, isTranslated: true },
                      },
                  )
                  .max(MaxAmount, {
                      label: l.MustBeSmallerThan,
                      fieldName: { value: CurrencyCode, isTranslated: true },
                      count: { value: +MaxAmount, isTranslated: true },
                  }),
          });
};

const CJBonusExpirationValidationSchema = () => {
    return Yup.object({
        [ExpirationLabels.HasExpiration]: Yup.boolean(),
        [ExpirationLabels.Type]: Yup.string().when([ExpirationLabels.HasExpiration], {
            is: (hasExpiration) => hasExpiration,
            then: (schema) => schema.required({ label: l.ExpirationTypeCannotBeEmpty }),
            otherwise: (schema) => schema.notRequired(),
        }),
        [ExpirationLabels.Period]: Yup.number().when([ExpirationLabels.HasExpiration, ExpirationLabels.Type], {
            is: (hasExpiration, expirationType) => hasExpiration && expirationType === ExpirationTypes.ExpirationPeriod,
            then: (schema) =>
                schema
                    .required({ label: l.CannotBeEmpty, fieldName: l.ExpirationPeriod })
                    .min(1, { label: l.ExpirationPeriodIsInvalid }),
            otherwise: (schema) => schema.notRequired(),
        }),
        [ExpirationLabels.Date]: Yup.string()
            .nullable()
            .when([ExpirationLabels.HasExpiration, ExpirationLabels.Type], {
                is: (hasExpiration, expirationType) =>
                    hasExpiration && expirationType === ExpirationTypes.ExpirationDate,
                then: (schema) =>
                    schema
                        .required({ label: l.CannotBeEmpty, fieldName: l.ExpirationDate })
                        .test(ExpirationLabels.Date, { label: l.IsInvalid, fieldName: l.ExpirationDate }, (value) => {
                            if (!value || value === 'Invalid date') return false;
                            const date = new Date(value);
                            return !isNaN(date.getTime());
                        })
                        .test(ExpirationLabels.Date, { label: l.IsInvalid, fieldName: l.ExpirationDate }, (value) => {
                            if (!value || value === 'Invalid date') return false;
                            const inputDate = new Date(value);
                            const now = new Date();
                            return inputDate.getTime() > now.getTime();
                        }),
                otherwise: (schema) => schema.notRequired(),
            }),
    });
};

const CJMenuItemDropDownValidationSchema = (option) => CJStaticTypeSchemaCreator(option);

const CJTagValidationSchema = (option) => CJStaticTypeSchemaCreator(option);

const CJScheduleValidationSchema = (option, node) => {
    const { Required } = option;
    return Yup.object({
        startDate: Yup.string()
            .test('startDate', { label: l.StartDateMustBeBiggerThanCurrentDate }, function (value) {
                const { startTime, timeZone, isActiveSchedule } = this.parent;

                if (
                    node?.data?.isDeletable === false ||
                    (!isActiveSchedule && !Required) ||
                    node?.data?.isEditOnlyLive === true
                ) {
                    return true;
                }

                const currentDate = new Date(
                    customMomentWithoutTimezoneConversion().format(MonthDayYearWithTimeFormat),
                );
                const chosenDate = new Date(
                    getDateFormat(
                        `${moment(value).format(MonthDayYearFormat)} ${startTime}`,
                        -timeZone,
                        MonthDayYearWithTimeFormat,
                    ),
                );
                return chosenDate.getTime() > currentDate.getTime();
            })
            .test('startDate', { label: l.StartDateMustBeSmallerThanEndDate }, function (value) {
                const { endDate, isActiveSchedule } = this.parent;
                if (!isActiveSchedule && !Required) {
                    return true;
                }
                const chosenStartDate = new Date(moment(value).format(MonthDayYearFormat));
                const chosenEndDate = new Date(`${moment(endDate).format(MonthDayYearFormat)} 23:59`);
                return chosenStartDate.getTime() < chosenEndDate.getTime();
            }),
        endDate: Yup.string().test('endDate', { label: l.EndDateMustBeBiggerThanStartDate }, function (value) {
            const { startDate, isActiveSchedule } = this.parent;
            if (!isActiveSchedule && !Required) {
                return true;
            }
            const chosenStartDate = new Date(moment(startDate).format(MonthDayYearFormat));
            const chosenEndDate = new Date(`${moment(value).format(MonthDayYearFormat)} 23:59`);
            return chosenStartDate.getTime() < chosenEndDate.getTime();
        }),
        datesDict: Yup.object().test('datesDict', { label: l.SelectAtLeastOneUnit }, function (value) {
            const { isActiveSchedule, modalView } = this.parent;
            if (!isActiveSchedule && !Required) {
                return true;
            }
            if (modalView === ScheduleModalViews.notRepeatView) return true;

            const isInvalid = isSchedulerUnitPairsInvalid(value[modalView]);

            return !isInvalid;
        }),
        startTime: Yup.string().test('startTime', { label: l.InvalidTime }, function (value: string) {
            const { isActiveSchedule } = this.parent;
            if (!isActiveSchedule && !Required) {
                return true;
            }
            const result = /^([0-2]?[0-9]):[0-5][0-9]$/.test(value);
            return result;
        }),
    });
};

const CJReferenceOptionValidationSchema = (
    elements,
    node,
    optionType,
    modelRelationScope,
    propertySettings,
    withoutType = false,
) =>
    Yup.object()
        .optionReference(elements, node, optionType, modelRelationScope, propertySettings, withoutType, '')
        .nullable(true);

const toMonthDayYearDate = (value: string | Date) => {
    if (value instanceof Date) {
        return moment(value).format(MonthDayYearFormat);
    }

    const parsedValue = moment(value, [MonthDayYearFormat, 'DD.MM.YYYY', moment.ISO_8601], true);
    return parsedValue.isValid()
        ? parsedValue.format(MonthDayYearFormat)
        : customMomentWithoutTimezoneConversion(value).format(MonthDayYearFormat);
};

const getDate = (value: string, timeZone: number) => {
    return new Date(
        getDateFormat(
            `${toMonthDayYearDate(value)} ${ZERO_TIME_WITHOUT_SECONDS}`,
            -timeZone,
            MonthDayYearWithTimeFormat,
        ),
    );
};

const getDateWithTime = (dateValue: string, timeValue: string | null, timeZone: number) => {
    if (!timeValue) return getDate(dateValue, timeZone);
    const [hours, minutes] = timeValue.split(':');
    const dateTime = moment(`${toMonthDayYearDate(dateValue)} ${hours}:${minutes}`, MonthDayYearWithTimeFormat, true);
    return new Date(getDateFormat(dateTime.format(MonthDayYearWithTimeFormat), -timeZone, MonthDayYearWithTimeFormat));
};

const getTimestampWithTimeZone = (dateValue: string, timeValue: string | null, timeZone: number | null) => {
    const safeTimeZone = timeZone ?? 0;
    const normalizedDate = toMonthDayYearDate(dateValue);
    const normalizedTime = timeValue ?? ZERO_TIME_WITHOUT_SECONDS;

    return customMomentWithTimezoneConversion(
        moment(`${normalizedDate} ${normalizedTime}`, MonthDayYearWithTimeFormat),
        safeTimeZone,
        true,
    ).valueOf();
};

const getCurrentTimestampInTimeZone = (timeZone: number | null) => {
    return customMomentWithTimezoneConversion(moment(), timeZone ?? 0).valueOf();
};

// Achievement Validation Schemas
// Every supported currency must carry an amount greater than zero.
const createCurrencyAmountsSchema = (t: TFunction, supportedCurrencies: PartnerCurrency[], fieldName: string) =>
    Yup.object()
        .nullable()
        .required(
            t(l.CannotBeEmpty, {
                fieldName,
            }),
        )
        .test(
            'all-supported-currencies',
            t(l.CannotBeEmpty, {
                fieldName,
            }),
            (value) => {
                if (!value) {
                    return false;
                }

                const amounts = value as CurrencyAmounts;

                return supportedCurrencies.every(({ CurrencyId }) => {
                    const amount = amounts[CurrencyId];

                    return amount !== undefined && amount.trim() !== '' && POSITIVE_AMOUNT_REGEXP.test(amount);
                });
            },
        );

const createChallengeSchema = (t: TFunction, supportedCurrencies: PartnerCurrency[]) =>
    Yup.object({
        productId: Yup.string()
            .nullable()
            .required(
                t(l.CannotBeEmpty, {
                    fieldName: t(l.Product),
                }),
            ),
        metricId: Yup.string()
            .nullable()
            .required(
                t(l.CannotBeEmpty, {
                    fieldName: t(l.Metric),
                }),
            ),

        selection: Yup.object({
            isSelectAll: Yup.boolean(),
            // Nothing has to be picked when the whole product is selected through the switcher
            data: Yup.array().when(['isSelectAll'], {
                is: (isSelectAll: boolean) => !isSelectAll,
                then: Yup.array().min(
                    1,
                    t(l.CannotBeEmpty, {
                        fieldName: t(l.Selection),
                    }),
                ),
            }),
        }),
        minAmounts: createCurrencyAmountsSchema(t, supportedCurrencies, t(l.MinAmount)),
        count: Yup.string()
            .nullable()
            .when('metricType', {
                is: MetricTypeEnum.Count,
                then: (schema) =>
                    schema
                        .required(
                            t(l.CannotBeEmpty, {
                                fieldName: t(l.Count),
                            }),
                        )
                        .test(
                            'is-positive-integer',
                            t(l.MustBePositiveInteger, {
                                fieldName: t(l.Count),
                            }),
                            isPositiveInteger,
                        ),
                otherwise: (schema) => schema.nullable(),
            }),

        amounts: Yup.mixed()
            .nullable()
            .when('metricType', {
                is: (metricType: MetricTypeEnum) => metricType === MetricTypeEnum.Money,

                then: () => createCurrencyAmountsSchema(t, supportedCurrencies, t(l.Amount)),

                otherwise: (schema) => schema.nullable(),
            }),
    });

export const AchievementValidationSchema = (t: TFunction, partnerSupportedCurrencies: PartnerCurrency[]) =>
    Yup.object({
        periodCount: Yup.string()
            .required(t(l.CannotBeEmpty, { fieldName: t(l.PeriodCount) }))
            .test(
                'is-positive-integer',
                t(l.MustBePositiveInteger, { fieldName: t(l.PeriodCount) }),
                isPositiveInteger,
            ),
        period: Yup.number()
            .nullable()
            .required(t(l.CannotBeEmpty, { fieldName: t(l.Period) })),
        challenges: Yup.array()
            .required(
                t(l.CannotBeEmpty, {
                    fieldName: t(l.Challenges),
                }),
            )
            .min(
                1,
                t(l.CannotBeEmpty, {
                    fieldName: t(l.Challenges),
                }),
            )
            .max(
                MAX_ACHIEVEMENT_CHALLENGES_COUNT,
                t(l.AchievementMaxChallengesCountExceeded, { count: MAX_ACHIEVEMENT_CHALLENGES_COUNT }),
            )
            .of(createChallengeSchema(t, partnerSupportedCurrencies)),
    });

export const CreateChallengeValidationSchema = (t: TFunction) =>
    Yup.object({
        productId: Yup.string()
            .nullable()
            .required(t(l.PleaseSelectAtLeastOneProductType, { fieldName: t(l.ProductType) })),
        metricId: Yup.string()
            .nullable()
            .required(t(l.CannotBeEmpty, { fieldName: t(l.ChallengeType) })),
    });

export const InformationStepValidationSchema = (
    t: TFunction,
    elements: unknown[] = [],
    isVisibleStartDate = true,
    isVisibleEndDate = true,
) => {
    const MINUTE = 60000;
    const OFFSET = 15;

    return Yup.object({
        isNonMarketing: Yup.boolean(),
        isPublic: Yup.boolean(),
        isActiveStartDate: Yup.boolean(),
        isActiveEndDate: Yup.boolean(),
        timeZone: Yup.number().nullable(),
        trackingPeriod: Yup.number()
            .nullable(true)
            .min(3, t(l.MustBeBiggerOrEqualThan, { fieldName: t(l.TrackingPeriod), count: 3 }))
            .max(14, t(l.MustBeSmallerOrEqualThan, { fieldName: t(l.TrackingPeriod), count: 14 }))
            .test('trackingPeriodRequired', t(l.CannotBeEmpty, { fieldName: t(l.TrackingPeriod) }), function (value) {
                const { metricId } = this.parent;

                return isNil(metricId) || (!isNil(value) && !Number.isNaN(value));
            }),
        metricId: Yup.number()
            .nullable(true)
            .test('metricRequired', t(l.CannotBeEmpty, { fieldName: t(l.Metric) }), function (value) {
                const { trackingPeriod } = this.parent;

                return isNil(trackingPeriod) || `${trackingPeriod}` === '' || !isNil(value);
            }),
        autoStartDate: Yup.string()
            .nullable()
            .when('isActiveStartDate', {
                is: (isActive: boolean) => isVisibleStartDate && isActive,
                then: Yup.string()
                    .nullable()
                    .required(t(l.CannotBeEmpty, { fieldName: t(l.StartDate) }))
                    .test(
                        'startDateMustBeBiggerThanCurrentDate',
                        t(l.StartDateMustBeBiggerThanCurrentDate),
                        function (value) {
                            if (isEmpty(value)) return true;
                            const { timeZone, autoStartTime } = this.parent;
                            if (isEmpty(autoStartTime)) return true;
                            const currentDateTimestamp = getCurrentTimestampInTimeZone(timeZone);
                            const chosenDateTimestamp = getTimestampWithTimeZone(value!, autoStartTime, timeZone);
                            return chosenDateTimestamp > currentDateTimestamp;
                        },
                    )
                    .test(
                        'startDateMustBeSmallerThanEndDate',
                        t(l.StartDateMustBeSmallerThanEndDateWithOffset, { offset: OFFSET }),
                        function (value) {
                            const { autoEndDate, isActiveEndDate, autoStartTime, autoEndTime, timeZone } = this.parent;
                            if (!(isVisibleEndDate && isActiveEndDate) || isEmpty(value) || isEmpty(autoEndDate)) {
                                return true;
                            }

                            if (!autoStartTime || !autoEndTime) {
                                return true;
                            }
                            const chosenStartDate = getDateWithTime(value!, autoStartTime, timeZone);
                            const chosenEndDate = getDateWithTime(autoEndDate, autoEndTime, timeZone);
                            return chosenStartDate.getTime() + OFFSET * MINUTE < chosenEndDate.getTime();
                        },
                    )
                    .startDateCJScheduleValidation(t, elements),
            }),
        autoStartTime: Yup.string()
            .nullable()
            .when('isActiveStartDate', {
                is: (isActive: boolean) => isVisibleStartDate && isActive,
                then: Yup.string()
                    .nullable()
                    .required(t(l.CannotBeEmpty, { fieldName: t(l.StartTime) }))
                    .test('startTime', l.InvalidTime, (value) => {
                        if (!value) return true;
                        return /^([0-2]?[0-9]):[0-5][0-9]$/.test(value);
                    }),
            }),
        autoEndDate: Yup.string()
            .nullable()
            .when('isActiveEndDate', {
                is: (isActive: boolean) => isVisibleEndDate && isActive,
                then: Yup.string()
                    .nullable()
                    .required(t(l.CannotBeEmpty, { fieldName: t(l.EndDate) }))
                    .test(
                        'endDateMustBeBiggerThanCurrentDate',
                        t(l.EndDateMustBeBiggerThanCurrentDateWithOffset, { offset: OFFSET }),
                        function (value) {
                            const { isActiveStartDate, autoEndTime, timeZone } = this.parent;
                            if (isVisibleStartDate && isActiveStartDate) return true;
                            if (isEmpty(value)) return true;
                            if (isEmpty(autoEndTime)) return true;
                            const currentDateTimestamp = getCurrentTimestampInTimeZone(timeZone);
                            const chosenDateTimestamp = getTimestampWithTimeZone(value!, autoEndTime, timeZone);
                            return chosenDateTimestamp > currentDateTimestamp + OFFSET * MINUTE;
                        },
                    )
                    .test(
                        'endDateMustBeBiggerThanStartDate',
                        t(l.EndDateMustBeBiggerThanStartDateWithOffset, { offset: OFFSET }),
                        function (value) {
                            const { autoStartDate, isActiveStartDate, autoStartTime, autoEndTime, timeZone } =
                                this.parent;
                            if (
                                !(isVisibleStartDate && isActiveStartDate) ||
                                isEmpty(value) ||
                                isEmpty(autoStartDate)
                            ) {
                                return true;
                            }

                            if (!autoStartTime || !autoEndTime) {
                                return true;
                            }
                            const chosenStartDate = getDateWithTime(autoStartDate, autoStartTime, timeZone);
                            const chosenEndDate = getDateWithTime(value!, autoEndTime, timeZone);
                            return chosenStartDate.getTime() + OFFSET * MINUTE < chosenEndDate.getTime();
                        },
                    )
                    .endDateCJScheduleValidation(t, elements),
            }),
        autoEndTime: Yup.string()
            .nullable()
            .when('isActiveEndDate', {
                is: (isActive: boolean) => isVisibleEndDate && isActive,
                then: Yup.string()
                    .nullable()
                    .required(t(l.CannotBeEmpty, { fieldName: t(l.EndTime) }))
                    .test('endTime', l.InvalidTime, (value) => {
                        if (!value) return true;
                        return /^([0-2]?[0-9]):[0-5][0-9]$/.test(value);
                    }),
            }),
    });
};

const CJListOptionValidationSchema = (option) => {
    const { CustomAttributes, Required } = option;
    return Required
        ? Yup.object({
              value: yupSchemaExtender(
                  Yup.array()
                      .required({
                          label: l.CannotBeEmpty,
                          fieldName: CustomAttributes?.DisplayName,
                      })
                      .min(1, {
                          label: l.CannotBeEmpty,
                          fieldName: CustomAttributes?.DisplayName,
                      })
                      .nullable(true),
                  option,
              ),
          })
        : null;
};

CJListOptionValidationSchema.displayName = 'CJListOptionValidationSchema';

const CJDateTimeOptionValidationSchema = (option) => {
    const { Required } = option;
    return Required
        ? Yup.object({
              date: Yup.string()
                  .required({
                      label: l.CannotBeEmpty,
                      fieldName: l.OptionValue,
                  })
                  .nullable(true), // schema for object
          })
        : null;
};

CJDateTimeOptionValidationSchema.displayName = 'CJDateTimeOptionValidationSchema';

const CJDaysLaterOptionValidationSchema = (option) => {
    const { Required } = option;
    return Required
        ? Yup.object({
              offset: Yup.number()
                  .required({
                      label: l.CannotBeEmpty,
                      fieldName: l.Offset,
                  })
                  .nullable(true),
          })
        : null;
};

CJDaysLaterOptionValidationSchema.displayName = 'CJDaysLaterOptionValidationSchema';

const CJThisDayOfYearOptionValidationSchema = (option) => {
    const { Required } = option;
    return Required
        ? Yup.object({
              offset: Yup.number()
                  .required({
                      label: l.CannotBeEmpty,
                      fieldName: l.Offset,
                  })
                  .nullable(true),
          })
        : null;
};

CJThisDayOfYearOptionValidationSchema.displayName = 'CJThisDayOfYearOptionValidationSchema';

const CJBlockNameOptionValidationSchema = (elements, node) =>
    Yup.object().blockIdValidation(elements, node).nullable(true);

const ReportScheduleValidationSchema = () => {
    return Yup.object({
        startDate: Yup.string()
            .test('startDate', { label: l.StartDateMustBeBiggerThanCurrentDate }, function (value) {
                const { startTime, timeZone } = this.parent;
                const currentDate = new Date(
                    customMomentWithoutTimezoneConversion().format(MonthDayYearWithTimeFormat),
                );
                const chosenDate = new Date(
                    getDateFormat(
                        `${moment(value).format(MonthDayYearFormat)} ${startTime}`,
                        -timeZone,
                        MonthDayYearWithTimeFormat,
                    ),
                );
                return chosenDate.getTime() > currentDate.getTime();
            })
            .test('startDate', { label: l.StartDateMustBeSmallerThanEndDate }, function (value) {
                const { endDate } = this.parent;

                const chosenStartDate = new Date(moment(value).format(MonthDayYearFormat));
                const chosenEndDate = new Date(`${moment(endDate).format(MonthDayYearFormat)} 23:59`);
                return chosenStartDate.getTime() < chosenEndDate.getTime();
            }),
        endDate: Yup.string().test('endDate', { label: l.EndDateMustBeBiggerThanStartDate }, function (value) {
            const { startDate } = this.parent;
            const chosenStartDate = new Date(moment(startDate).format(MonthDayYearFormat));
            const chosenEndDate = new Date(`${moment(value).format(MonthDayYearFormat)} 23:59`);
            return chosenStartDate.getTime() < chosenEndDate.getTime();
        }),
        selectedFileFormats: Yup.array().test(
            'selectedFileFormats',
            { label: l.CannotBeEmpty, fieldName: l.ExportFileFormat },
            function (value) {
                const { selectedFtps, selectedEmails } = this.parent;
                if (isEmpty(selectedFtps) && isEmpty(selectedEmails)) {
                    return true;
                }
                return !isEmpty(value);
            },
        ),
    });
};

const isValidTime = (value) => {
    if (!isEmpty(value)) {
        // value must be like '00:00'
        const data = value.split(':');
        if (data.length !== 2) {
            return false;
        }

        if (isNil(data[0].match('^(2[0-3]|[01]?[0-9])$')) || isNil(data[1].match('^([0-5]?[0-9])$'))) {
            return false;
        }
    }
    return true;
};

const CJCommunicationAvailabilityConditionTimePickerValidationSchema = (fieldName, checkValue) => {
    const minute = 60000;
    const offset = 15;

    const getDate = (time) => {
        const date = new Date();
        const data = time.split(':');

        date.setHours(+data[0], +data[1]);
        return date;
    };

    const checkTimes = (value, checkValue) => {
        if (!isEmpty(value) && !isEmpty(checkValue) && isValidTime(value) && isValidTime(checkValue)) {
            const chosenValue = getDate(value);
            const chosenCheckValue = getDate(checkValue);

            return chosenValue.getTime() + offset * minute <= chosenCheckValue.getTime();
        }

        return true;
    };

    return Yup.object({
        value: Yup.string()
            .required({
                label: l.CannotBeEmpty,
                fieldName:
                    fieldName === 'StartTime'
                        ? l.CommunicationAvailabilityStartTime
                        : l.CommunicationAvailabilityEndTime,
            })
            .test('communicationAvailabilityConditionTimeFormat', { label: l.InvalidTime }, isValidTime)
            .when([], {
                is: () => fieldName === 'StartTime',
                then: Yup.string()
                    .test(
                        'communicationAvailabilityConditionStartTimeMustBeSmallerThenEndTime',
                        { label: l.StartTimeMustBeSmallerThenEndTimeWithOffset, offset: { value: offset } },
                        (value) => {
                            return checkTimes(value, checkValue);
                        },
                    )
                    .nullable(true),
                otherwise: Yup.string()
                    .test(
                        'communicationAvailabilityConditionEndTimeMustBeBiggerThenStartTime',
                        { label: l.EndTimeMustBeBiggerThenStartTimeWithOffset, offset: { value: offset } },
                        (value) => {
                            return checkTimes(checkValue, value);
                        },
                    )
                    .nullable(true),
            })
            .nullable(true),
    }).nullable(true);
};

const CJCommunicationAvailabilityConditionOptionValidationSchema = (option) => {
    const { Required } = option;
    return Yup.object({
        timeZone: Yup.number()
            .when(['scheduleType'], {
                is: (scheduleType) =>
                    scheduleType !== CommunicationAvailabilityScheduleTypeLabels.Always && !isNil(scheduleType),
                then: Yup.number().required({
                    label: l.CannotBeEmpty,
                    fieldName: l.TimeZone,
                }),
            })
            .nullable(true),
        scheduleType: Yup.number().nullable(true),
        schedule: Yup.array()
            .when(['scheduleType'], {
                is: (scheduleType) => scheduleType !== CommunicationAvailabilityScheduleTypeLabels.Always,
                then: Yup.array().test(
                    'communicationAvailabilityConditionSchedule',
                    { label: l.CheckCommunicationAvailabilityScheduleDates },
                    function (value) {
                        if (Required && isEmpty(value)) {
                            return false;
                        }

                        for (let index = 0; index < (value ? value.length : 0); index++) {
                            const item = value?.[index];
                            if (item.isEnable) {
                                let isValid = yupSchemaValidate(
                                    CJCommunicationAvailabilityConditionTimePickerValidationSchema(
                                        'StartTime',
                                        item.value.endTime?.value,
                                    ),
                                    { value: item.value.startTime?.value },
                                    noop,
                                );

                                if (!isValid) {
                                    return false;
                                }
                                isValid = yupSchemaValidate(
                                    CJCommunicationAvailabilityConditionTimePickerValidationSchema(
                                        'EndTime',
                                        item.value.startTime?.value,
                                    ),
                                    { value: item.value.endTime?.value },
                                    noop,
                                );

                                if (!isValid) {
                                    return false;
                                }
                            }
                        }

                        return true;
                    },
                ),
            })
            .nullable(true),
    }).nullable(true);
};

const CJCalculateKPIValidationSchema = (option) => {
    const { Required } = option;

    return Required
        ? Yup.object({
              segmentId: Yup.string().required({
                  label: l.CannotBeEmpty,
                  fieldName: l.Segment,
              }),
              objectType: Yup.string().when(['amountSourceType'], {
                  is: (amountSourceType) => amountSourceType === DynamicBonusTypes.Object,
                  then: Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.CJBlockCalculateKPIKind,
                  }),
              }),
              columnInfoId: Yup.string().when(['amountSourceType'], {
                  is: (amountSourceType) => amountSourceType === DynamicBonusTypes.KPI,
                  then: Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.CJBlockCalculateKPIKind,
                  }),
              }),
              calculationMethod: Yup.string().when(['amountSourceType'], {
                  is: (amountSourceType) => amountSourceType === DynamicBonusTypes.Object,
                  then: Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.CJBlockCalculateKPICalculateMethod,
                  }),
              }),
          })
        : Yup.object({
              segmentId: Yup.string().nullable(true),
              objectType: Yup.string().when(['segmentId', 'amountSourceType'], {
                  is: (segmentId, amountSourceType) =>
                      !isEmpty(segmentId) && amountSourceType === DynamicBonusTypes.Object,
                  then: Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.CJBlockCalculateKPIKind,
                  }),
              }),
              columnInfoId: Yup.string().when(['segmentId', 'amountSourceType'], {
                  is: (segmentId, amountSourceType) =>
                      !isEmpty(segmentId) && amountSourceType === DynamicBonusTypes.KPI,
                  then: Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.CJBlockCalculateKPIKind,
                  }),
              }),
              calculationMethod: Yup.string().when(['segmentId', 'amountSourceType'], {
                  is: (segmentId, amountSourceType) =>
                      !isEmpty(segmentId) && amountSourceType === DynamicBonusTypes.Object,
                  then: Yup.string().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.CJBlockCalculateKPICalculateMethod,
                  }),
              }),
          });
};

const CJSubscriptionsValidationSchema = (option) => {
    const { Required } = option;

    return Required
        ? Yup.object({
              channel: Yup.number().required({
                  label: l.CannotBeEmpty,
                  fieldName: l.Channels,
              }),
              category: Yup.number().when(['allowCategory'], {
                  is: (allowCategory) => allowCategory,
                  then: Yup.number().required({
                      label: l.CannotBeEmpty,
                      fieldName: l.Category,
                  }),
              }),
          })
        : null;
};

export {
    CJDoubleValidationSchema,
    CJIntegerValidationSchema,
    CJTemplateIdValidationSchema,
    CJStringValidationSchema,
    CJTimeStampValidationSchema,
    CJMoneyValidationSchema,
    CJProviderIdValidationSchema,
    CJSegmentIdValidationSchema,
    CJBestMessageSenderValidationSchema,
    CJPartnerBonusIdValidationSchema,
    CJBonusAmountInputValidationSchema,
    CJBonusExpirationValidationSchema,
    CJMenuItemDropDownValidationSchema,
    CJTagValidationSchema,
    CJScheduleValidationSchema,
    CJReferenceOptionValidationSchema,
    CJListOptionValidationSchema,
    CJDateTimeOptionValidationSchema,
    CJDaysLaterOptionValidationSchema,
    CJThisDayOfYearOptionValidationSchema,
    CJVisualizationStepTemplateValidationSchema,
    CJVisualizationStepIconValidationSchema,
    CJVisualizationButtonValidationSchema,
    CJVisualizationProgressTitleValidationSchema,
    CJBlockNameOptionValidationSchema,
    ReportScheduleValidationSchema,
    // CJPreSaveModalBodyContentValidationSchema,
    CJCommunicationAvailabilityConditionTimePickerValidationSchema,
    CJCommunicationAvailabilityConditionOptionValidationSchema,
    CJCalculateKPIValidationSchema,
    CJSubscriptionsValidationSchema,
};
